// M5 parts of the Stage 1 card footer (prototype cardFoot): the "Approval needed" boxes in Approval Pending
// (AV change, OCR below 10%, Change Unit) with Approve / Reject Changes / Approve OCR, and the CRM's next button
// (Preview and Approve in CRM Review, Select Parking in Parking Selection). Data from GET /crm2/list (approvals,
// changeUnit); the buttons call onAct(card, what, extra) and Crm2Page opens the pop-up.
import React from "react";
import css from "../ui/Crm2Cards.module.css";

const inr = (n) => `₹ ${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const sign = (d) => <span style={{ color: d < 0 ? "#B42318" : "#027A48" }}>{`${d < 0 ? "− " : "+ "}${inr(Math.abs(d))}`}</span>;

function Box({ title, sub, rows }) {
  return (
    <div style={{ width: "100%", border: "1px solid #FEDF89", background: "#FFFAEB", borderRadius: ".8rem", padding: "1.2rem 1.4rem", display: "grid", gap: ".6rem", boxSizing: "border-box" }}>
      <div>
        <b style={{ color: "#B54708", fontSize: "1.5rem" }}>Approval needed: {title}</b>
        <p className={css.hint} style={{ margin: ".2rem 0 0" }}>
          {sub}
        </p>
      </div>
      {rows.map(([k, v]) => (
        <div key={k} className={css.row} style={{ justifyContent: "space-between", gap: "1rem", fontSize: "1.35rem", flexWrap: "nowrap" }}>
          <span style={{ color: "#475467" }}>{k}</span>
          <span style={{ color: "#101828", fontWeight: 500, textAlign: "right" }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

const planName = (p) => p?.Description || p?.description || p?.Name || null;

// can = { approve, approveOcr, changeUnit } (Changed Plan › Approve / Approve OCR; Change Unit approver)
export function ApprovalBoxes({ c, can = {}, onAct }) {
  const out = [];
  for (const a of c.approvals || []) {
    if (a.type === "AV_CHANGE") {
      const o = a.oldUnitCost || {};
      const n = a.newUnitCost || {};
      const oldPlan = planName(a.oldPaymentPlan);
      const newPlan = planName(a.newPaymentPlan);
      const planCh = newPlan && newPlan !== oldPlan;
      // Old parking flow (M5-F6): the typed parking upgrade is part of the new agreement value
      const pu = Number(a.parkingUpgradeRequested) || 0;
      const avOnly = (Number(n.bsp) || 0) - pu !== (Number(o.bsp) || 0);
      out.push(
        <div key={a.crm2ApprovalId} style={{ display: "flex", flexDirection: "column", gap: ".8rem", width: "100%" }}>
          <Box
            title={`${pu ? (avOnly ? "AV change + parking upgrade" : "Parking upgrade") : "AV change"}${planCh ? " + payment plan change" : ""}`}
            sub={pu ? `Requested by ${a.requestedBy || "the CRM"} (CRM) · Parking flow: Old · the reporting manager can edit the parking amount in Approve` : `Requested by ${a.requestedBy || "the CRM"} (CRM) · the CRM changed the agreement value`}
            rows={[
              ...(pu ? [["Parking upgrade", <b>{inr(pu)}</b>]] : []),
              ["Agreement value", <>{inr(o.bsp)} → <b>{inr(n.bsp)}</b></>],
              ["Difference", sign((Number(n.bsp) || 0) - (Number(o.bsp) || 0))],
              ["Payment plan", planCh ? <>{oldPlan || "—"} → <b>{newPlan}</b></> : `${newPlan || oldPlan || "—"} (no change)`],
              ["Remark", a.remark || "—"],
            ]}
          />
          {can.approve && (
            <div className={css.row} style={{ justifyContent: "space-between" }}>
              <button type="button" className={`${css.btn} ${css.btnCard}`} style={{ flex: "none" }} onClick={() => onAct(c, "avApprove", a)}>
                Approve
              </button>
              <button type="button" className={`${css.btn} ${css.btnDanger}`} style={{ flex: "none" }} onClick={() => onAct(c, "avReject", a)}>
                Reject Changes
              </button>
            </div>
          )}
          <p className={css.hint} style={{ margin: 0, width: "100%" }}>
            Pending with {a.pendingWith || "the CRM's reporting manager"}. Buttons shown to the CRM Head and users with the Changed Plan permission (as today).
          </p>
        </div>,
      );
    } else if (a.type === "OCR_BELOW_MIN") {
      const av = Number(c.unitCost?.bsp) || 0;
      const need = (av * (Number(a.ocrRequiredPercent) || 10)) / 100;
      const paid = Number(a.ocrPaid) || 0;
      out.push(
        <div key={a.crm2ApprovalId} style={{ display: "flex", flexDirection: "column", gap: ".8rem", width: "100%" }}>
          <Box
            title={`OCR below ${Number(a.ocrRequiredPercent) || 10}%`}
            sub="The customer paid less than 10% of the agreement value as OCR"
            rows={[
              ["Agreement value", inr(av)],
              ["OCR paid", <><b>{inr(paid)}</b> ({av ? ((paid * 100) / av).toFixed(2) : "0.00"}%)</>],
              [`${Number(a.ocrRequiredPercent) || 10}% OCR needed`, inr(need)],
              ["Short by", <span style={{ color: "#B42318" }}>{inr(Math.max(need - paid, 0))}</span>],
              ["Payment plan", planName(a.newPaymentPlan) || c.planName || "—"],
            ]}
          />
          {can.approveOcr && (
            <div className={css.row}>
              <button type="button" className={`${css.btn} ${css.btnCard}`} style={{ flex: "none" }} onClick={() => onAct(c, "ocrApprove", a)}>
                Approve OCR
              </button>
            </div>
          )}
          <p className={css.hint} style={{ margin: 0, width: "100%" }}>
            Pending with {a.pendingWith || "the CRM's reporting manager"}; open to users with Changed Plan › Approve OCR (as today). After approval: Parking Selection if the plan is approved, else CRM Review.
          </p>
        </div>,
      );
    }
  }
  const q = c.changeUnit;
  if (q) {
    const d = (Number(q.newUnitPrice?.bsp) || 0) - (Number(q.oldUnitPrice?.bsp) || 0);
    out.push(
      <div key={q.changeUnitRequestId} style={{ display: "flex", flexDirection: "column", gap: ".8rem", width: "100%" }}>
        <Box
          title="Change Unit"
          sub={`Requested by ${q.raisedBy || "the CRM"} (CRM) · same project · before the allotment letter`}
          rows={[
            ["Unit", <>{q.from} → <b>{q.to}</b></>],
            ["Agreement value", <>{inr(q.oldUnitPrice?.bsp)} → <b>{inr(q.newUnitPrice?.bsp)}</b></>],
            ["Difference", sign(d)],
            ["Description", q.reason || "—"],
          ]}
        />
        {can.changeUnit && (
          <div className={css.row} style={{ justifyContent: "space-between" }}>
            <button type="button" className={`${css.btn} ${css.btnCard}`} style={{ flex: "none" }} onClick={() => onAct(c, "cuApprove", q)}>
              Approve
            </button>
            <button type="button" className={`${css.btn} ${css.btnDanger}`} style={{ flex: "none" }} onClick={() => onAct(c, "cuReject", q)}>
              Reject
            </button>
          </div>
        )}
        <p className={css.hint} style={{ margin: 0, width: "100%" }}>
          Pending with {q.pendingWith || "the CRM's reporting manager"} (as today). The 15 days do not restart.
        </p>
      </div>,
    );
  }
  if (!out.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem", width: "100%" }}>
      {c.planName && <p style={{ fontSize: "1.4rem", color: "rgba(220,104,3,1)", width: "100%", margin: 0 }}>Payment Plan {c.planName} is selected.</p>}
      {out}
    </div>
  );
}

// the CRM's next button: CRM Review → Preview and Approve (or Preview), Parking Selection → Select Parking
export function nextButton(c, can = {}, onAct) {
  if (c.sub === "CRM Review" && (can.previewApprove || can.preview)) {
    return (
      <button type="button" className={`${css.btn} ${css.btnCard}`} style={{ flex: "none" }} onClick={() => onAct(c, can.previewApprove ? "plan" : "planView")}>
        {can.previewApprove ? "Preview and Approve" : "Preview"}
      </button>
    );
  }
  if (c.sub === "Parking Selection" && can.selectParking) {
    return (
      <button type="button" className={`${css.btn} ${css.btnCard}`} style={{ flex: "none" }} onClick={() => onAct(c, "parking")}>
        Select Parking
      </button>
    );
  }
  return null;
}

// one line under the meta once parking is chosen (DC-88): "#12 Included · Podium 1 · Stack Parking"
export function parkingText(c) {
  if (!c.parking?.length) return c.isParkingSelectionSubmitted && c.sub === "Final Review" ? "No parking for this unit" : null;
  // Old flow slots have no price of their own (the approved upgrade is in the agreement value)
  return c.parking.map((p) => `#${p.number} ${p.upgraded ? `Upgraded${Number(p.price) > 0 ? ` (${inr(p.price)})` : ""}` : "Included"} · ${[p.floor, p.type].filter(Boolean).join(" · ")}`).join(", ");
}
